import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';

import { renderTag } from 'hb-builder/components/BuilderComponents/dynamic/EmailList/EmailListLayouts/utils';
import type { EmailPublicationDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/EmailPublication/types';
import type { Event } from 'hb-builder/components/BuilderComponents/dynamic/types/event';
import EmailPublicationNewsLoader from 'hb-builder/components/common/LoadingComponents/EmailPublicationNewsLoader';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import PublicationText from 'hb-react/shared/components/Publication/PublicationText';

export interface EventsWrapperProps {
  data: Event;
  dynamicAttributes: EmailPublicationDynamicAttributes;
  isLoading: boolean;
}

const EventsWrapper = ({
  data,
  dynamicAttributes,
  isLoading,
}: EventsWrapperProps) => {
  if (isLoading) {
    return <EmailPublicationNewsLoader />;
  }

  const {
    displayAttendees,
    displayCategories,
    displayContent,
    displayCoverPicture,
    displayDate,
    displayLocation,
    displayOnlineEvent,
    displayTitle,
  } = dynamicAttributes.fields;

  return (
    <WrapperStyled>
      {displayCoverPicture && (
        <ImageWrapper
          alt="cover event"
          data-testid="builder-cover-wrapper"
          src={data.cover_picture}
        />
      )}
      <ContentWrapper>
        {displayTitle && (
          <TitleWrapper>
            <ClampedLinkWorkaround
              clamp={2}
              lineHeight=""
              margin={0}
              padding={0}
              useNativeClamp
            >
              {data.title}
            </ClampedLinkWorkaround>
          </TitleWrapper>
        )}

        {displayCategories && data.network_categories?.length > 0 && (
          <Box fontSize="12px">
            {data.network_categories.map((category, i) =>
              renderTag(
                category.name,
                data.network_categories.length === i + 1,
              ),
            )}
          </Box>
        )}

        {displayDate && data.start_date && (
          <Box display="bock" mt="15px">
            <Label>{__('Date:')}</Label>
            {data.start_date}
          </Box>
        )}

        {displayLocation && data.locations && (
          <EventItem>
            <Label>{__('Location:')}</Label>
            {data.locations.address}
          </EventItem>
        )}

        {displayAttendees && (
          <EventItem>
            <Label>{__('Attendees:')}</Label>
            {data.total_attendees}
          </EventItem>
        )}

        {displayOnlineEvent && (
          <EventItem>
            <Label>
              {data.webinar_url ? __('Online event') : __('On site event')}
            </Label>
          </EventItem>
        )}

        {displayContent && data.email_description && (
          <>
            <Divider />
            <PublicationText text={data.email_description} />
          </>
        )}
      </ContentWrapper>
    </WrapperStyled>
  );
};

export default EventsWrapper;

const WrapperStyled = styled(Box).attrs(() => ({
  color: 'black',
  flexDirection: 'column',
  width: '100%',
}))``;

const ImageWrapper = styled.img`
  height: 100px;
  margin: 16px 0 0 16px;
  object-fit: cover;
  position: relative;
  width: 205px;
`;

const ContentWrapper = styled(Box).attrs(() => ({
  display: 'block',
  p: 4,
}))`
  p {
    margin: 0 0 12px 0;
  }

  li p {
    display: inline-block;
    margin: 0;
  }

  ul,
  ol {
    list-style-position: inside;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TitleWrapper = styled(Box).attrs(() => ({
  color: 'black',
  display: 'block',
  fontSize: 9,
  fontWeight: 0,
  lineHeight: 9,
  mb: '15px',
}))``;

const EventItem = styled(Box).attrs(() => ({
  display: 'block',
  mt: 2,
}))``;

const Label = styled.span`
  font-size: 14px;
  font-weight: bolder;
  margin-right: 4px;
`;
